import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MyButton from 'components/ui/Button/MyButton';
import { Form, Input, Card, Checkbox, Tooltip } from 'antd';
import { noBlankSpacesValidator, areValuesNotEmptyExceptKeys } from 'components/common/validation';
import ToastMsg from "components/common/ToastMsg";
import { CommonCancelPopup } from "components/common/CommonComponent";
import useApiMethods from 'hooks/apiConfig/method/apiMethods';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { ImagePaths } from 'utils/ImagePath';
import { NEW_LETTER_TEMPLATE_API } from 'constant';

export const AddLetterTemplate = () => {
    const companyid = sessionStorage.getItem("customConfig") ? JSON.parse(sessionStorage.getItem("customConfig")).company_id : '';
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        content: '',
        is_active: true,
        company_id: companyid
    });
    const [loadings, setLoadings] = useState(false);
    const [isCancelClk, setIsCancelClk] = useState(false);
    const prevPropsRef = useRef();
    const { create, update } = useApiMethods();

    const navigate = useNavigate();
    const location = useLocation();
    const { fromEdit, editDetails, isActive } = location.state || {};
    const [form] = Form.useForm();

    const letterVariables = useMemo(() => [
        { key: "employee_name", label: "Employee Name" },
        { key: "designation", label: "Designation" },
        { key: "joining_date", label: "Joining Date" },
        { key: "salary", label: "Salary" },
        { key: "company_name", label: "Company Name" },
    ], []);

    const [searchTerm, setSearchTerm] = useState("");
    const quillRef = useRef(null);

    const validVariableTags = useMemo(() => 
        letterVariables.map((v) => `{${v.key}}`),
        [letterVariables]
    );

    useEffect(() => {
        if (
            editDetails &&
            Object.keys(editDetails).length &&
            JSON.stringify(prevPropsRef.current) !== JSON.stringify(editDetails)
        ) {
            const initial = {
                id: editDetails.id,
                code: editDetails.code || "",
                name: editDetails.name || "",
                description: editDetails.description || "",
                content: editDetails.content || "",
                is_active: editDetails.is_active,
                company_id: editDetails.company_id,
            };
            setFormData(initial);
            form.setFieldsValue(initial);
            prevPropsRef.current = editDetails;
        }
    }, [editDetails, form]);    

    const hasEditedVariable = useCallback((content) => {
        if (!content) return false;
        
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content;
        const textContent = tempDiv.textContent || tempDiv.innerText || '';

        const usedVariables = textContent.match(/{(.*?)}/g) || [];

        for (let variable of usedVariables) {
            if (!validVariableTags.includes(variable)) {
                return true;
            }
        }

        return false;
    }, [validVariableTags]);

    const handleChange = useCallback((name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value,
        }));
        form.setFieldsValue({
            [name]: value,
        });
    }, [form]);

    const handleBackCancel = useCallback(() => {
        navigate('/master/lettertemplate');
    }, [navigate]);

    const handleCancelClick = useCallback(() => {
        setIsCancelClk(false);
        handleBackCancel();
    }, [handleBackCancel]);

    const handleSubmit = useCallback(async (isDraftClked) => {
        setLoadings(true);
        
        if (!formData.name) {
            ToastMsg("error", "Template Name is required!");
            setLoadings(false);
            return;
        }

        const updatedFormData = {
            ...formData,
            name: formData.name ? formData.name : null,
            description: formData.description ? formData.description : null,
            content: formData.content ? formData.content : '',
            is_draft: !!isDraftClked,
            company: formData.company_id ? formData.company_id : null,
        };

        if (fromEdit) {
            updatedFormData.code = formData?.code ? formData.code : null;
        } else {
            delete updatedFormData.code;
        }

        try {
            let isFormData = false;
            const response = fromEdit
                ? await update(NEW_LETTER_TEMPLATE_API, updatedFormData, isFormData)
                : await create(NEW_LETTER_TEMPLATE_API, updatedFormData, isFormData);

            if (response?.status === "success") {
                ToastMsg("success", response.message);
                handleBackCancel();
                setFormData({
                    code: '',
                    name: '',
                    description: '',
                    content: '',
                    is_active: true,
                    company_id: companyid
                });
            } else if (response?.status === "fail") {
                ToastMsg("error", response.message);
            }
        } catch (error) {
            ToastMsg("error", error.message || "An error occurred.");
        } finally {
            setLoadings(false);
            setIsCancelClk(false);
        }
    }, [formData, fromEdit, handleBackCancel, update, create]);

    const checkAllMandatory = useCallback((isDraftClked) => {
        if (!isDraftClked) {
            form
                .validateFields()
                .then(() => {
                    handleSubmit(false);
                })
                .catch(error => {
                    console.error("Validation Error:", error);
                });
        } else {
            handleSubmit(true);
        }
    }, [form, handleSubmit]);

    const handleSave = useCallback(() => {
        checkAllMandatory(false);
    }, [checkAllMandatory]);

    const handleSaveAsDraft = useCallback(() => {
        checkAllMandatory(true);
    }, [checkAllMandatory]);

    const isDataAvail = useMemo(() =>
        areValuesNotEmptyExceptKeys(formData, ['is_active', 'company_id']),
        [formData]
    );

    const insertVariable = useCallback((variable) => {
        if (quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection();
            
            if (range) {
                editor.insertText(range.index, variable);
                editor.setSelection(range.index + variable.length);
            } else {
                const length = editor.getLength();
                editor.insertText(length - 1, variable);
                editor.setSelection(length + variable.length - 1);
            }
        }
    }, []);

    const filteredVariables = useMemo(() => 
        letterVariables.filter((v) =>
            v.label.toLowerCase().includes(searchTerm.toLowerCase())
        ),
        [letterVariables, searchTerm]
    );

    const modules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ indent: "-1" }, { indent: "+1" }],
        ],
    }), []);

    return (
        <Card className="h-full shadow-lg rounded-xl border border-gray-200">
            {/* Header */}
            <div className="h-1/6 flex justify-between items-center px-2 py-2 border-b border-gray-300">
                <div className="flex items-center space-x-1">
                    <Tooltip title="Back" placement="bottom">
                        <img
                            src={ImagePaths.backArrow.default}
                            alt="Go Back"
                            className="commonTblBackIcon cursor-pointer"
                            onClick={handleBackCancel}
                        />
                    </Tooltip>
                    <p className="text-lg font-semibold text-gray-800">
                        {fromEdit ? "Edit Letter Template" : "Add Letter Template"}
                    </p>
                </div>
                <Checkbox
                    disabled={fromEdit ? !isActive : false}
                    checked={formData.is_active}
                    onChange={(e) => handleChange("is_active", e.target.checked)}
                >
                    Active
                </Checkbox>
            </div>

            {/* Form Body */}
            <div className="h-5/6 px-4 py-4 overflow-y-auto">
                <Form
                    layout="vertical"
                    form={form}
                    colon={false}
                    requiredMark={false}
                    name="letter_template_form"
                    onFinish={handleSave}
                    initialValues={formData}
                    onSubmit={(e) => e.preventDefault()}
                    className="w-full"
                >
                    <div className="flex flex-wrap w-full">
                        {/* Code - Only in Edit */}
                        {fromEdit && (
                            <Form.Item
                                label="Letter Policy Code"
                                name="code"
                                style={styles.formItem}
                                rules={[
                                    { required: true, message: "Code is required" },
                                    { max: 10, message: "Max 10 characters" },
                                    { validator: noBlankSpacesValidator },
                                ]}
                            >
                                <Input
                                    maxLength={10}
                                    placeholder="Enter code"
                                    onChange={(e) => handleChange("code", e.target.value)}
                                    disabled={fromEdit}
                                />
                            </Form.Item>
                        )}

                        {/* Name */}
                        <Form.Item
                            label={
                                <span>
                                    Letter Template Name <span className="requiredTxt">*</span>
                                </span>
                            }
                            name="name"
                            style={styles.formItem}
                            rules={[
                                { required: true, message: "Name is required" },
                                { max: 50, message: "Max 50 characters" },
                                { validator: noBlankSpacesValidator },
                            ]}
                        >
                            <Input
                                maxLength={50}
                                placeholder="Enter template name"
                                onChange={(e) => handleChange("name", e.target.value)}
                            />
                        </Form.Item>

                        {/* Description */}
                        <Form.Item
                            label={
                                <span>
                                    Letter Template Description <span className="requiredTxt">*</span>
                                </span>
                            }
                            name="description"
                            style={styles.formItem}
                            rules={[
                                { required: true, message: "Description is required" },
                                { max: 150, message: "Max 150 characters" },
                                { validator: noBlankSpacesValidator },
                            ]}
                        >
                            <Input
                                maxLength={150}
                                placeholder="Enter description"
                                onChange={(e) => handleChange("description", e.target.value)}
                            />
                        </Form.Item>
                    </div>

                    <div className="mb-4">
                        <span className="text-xs font-semibold text-gray-600">Insert Variable:</span>
                        {letterVariables.length > 5 && (
                            <Input
                                placeholder="Search variable..."
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="mb-2 mt-1 max-w-xs"
                            />
                        )}

                        <div className="flex flex-wrap gap-2 mt-2 max-h-28 overflow-y-auto">
                            {filteredVariables.map((v) => (
                                <button
                                    type='button'
                                    key={v.key}
                                    className="bg-blue-50 cursor-pointer text-blue-800 text-xs px-2 py-1 rounded-full border border-blue-300 hover:bg-blue-100 transition"
                                    onClick={() => insertVariable(`{${v.key}}`)}
                                >
                                    {`${v.label}`}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <Form.Item
                        label={
                            <span>
                                Content <span className="requiredTxt">*</span>
                            </span>
                        }
                        name="content"
                        style={{ width: "100%", maxWidth: "650px" }}
                        rules={[
                            { required: true, message: "Content is required" },
                            {
                                validator: (_, value) => {
                                    if (!value) return Promise.reject("Content is required");
                                    
                                    const plainText = value.replace(/<(.|\n)*?>/g, "").trim();
                                    if (plainText.length === 0) {
                                        return Promise.reject("Content should not be empty");
                                    }
                                    
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <ReactQuill
                            ref={quillRef}
                            theme="snow"
                            value={formData.content}
                            onChange={(value) => {
                                handleChange("content", value);
                                if (hasEditedVariable(value)) {
                                    ToastMsg("warning", "You have edited a variable! This may affect the offer template.");
                                }
                            }}
                            placeholder="Write your letter template content here..."
                            style={{ height: "200px", marginBottom: "40px" }}
                            modules={modules}
                            onKeyDown={(e) => {
                                if (e.key === 'Tab') {
                                    e.preventDefault();
                                    const editor = quillRef.current?.getEditor();
                                    if (editor) {
                                        editor.insertText(editor.getSelection()?.index || 0, '\t');
                                    }
                                }
                            }}
                        />
                    </Form.Item>

                    {/* Buttons */}
                    <div className="flex justify-end mt-6">
                        <MyButton
                            htmlType="button"
                            label="Cancel"
                            outlined
                            onClick={
                                isDataAvail && !fromEdit
                                    ? () => setIsCancelClk(true)
                                    : handleBackCancel
                            }
                            paddingX="0 1.2vw"
                            marginRight="0.625vw"
                        />
                        {!fromEdit && (
                            <MyButton
                                htmlType="button"
                                label="Draft"
                                onClick={handleSaveAsDraft}
                                disabled={!isDataAvail}
                                bgColor={isDataAvail ? "#334B49" : "#cbcbcb"}
                                loading={loadings}
                                paddingX="0 1.2vw"
                                marginRight="0.625vw"
                            />
                        )}
                        <MyButton
                            htmlType="submit"
                            label={fromEdit ? "Update" : "Save"}
                            loading={loadings}
                            paddingX="0 1.2vw"
                        />
                    </div>
                </Form>
            </div>

            {/* Cancel Popup */}
            {isCancelClk && (
                <CommonCancelPopup
                    handleCancelBtn={handleCancelClick}
                    handleDraftBtn={handleSaveAsDraft}
                />
            )}
        </Card>
    );
};

const styles = {
    textField: {
        width: "23vw"
    },
    formItem: {
        width: "23vw",
        margin: "1vw"
    },
    emptyFormItem: {
        width: "23vw",
        margin: "1vw"
    }
};

export default AddLetterTemplate;